import { gql, NetworkStatus, useApolloClient } from "@apollo/client";
import { faCloudDownloadAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ErrorMessage } from "@msys/ui";
import { Icon, IconButton, Tooltip } from "@mui/material";
import { useState } from "react";
import {
  OpenMasterdataAvailabilityDocument,
  OpenMasterdataAvailabilityQuery,
  OpenMasterdataAvailabilityQueryVariables,
  useOpenMasterdataAvailabilityQuery,
  useOpenMasterdataUpdateSupplierProductMutation,
} from "./SyncViaOpenMasterdataButton.generated.js";
import { useTranslate } from "@tolgee/react";
import { SyncViaOpenMasterdataAuthenticationModal } from "../modals/SyncViaOpenMasterdataAuthenticationModal.js";
import { ProductOverview__ProductFragment } from "../Product.generated.js";

export const SyncViaOpenMasterdataButton = ({
  product,
  canEdit,
  refetchQueries,
}: {
  product: ProductOverview__ProductFragment;
  canEdit?: boolean;
  refetchQueries?: string[];
}) => {
  const { t } = useTranslate(["Product", "Global"]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const itekCompanyId = product.supplier.itekCompanyIdentifier;

  const client = useApolloClient();

  const query = useOpenMasterdataAvailabilityQuery({
    client,
    variables: {
      itekCompanyId: itekCompanyId!,
    },
    fetchPolicy: "network-only",
    skip: !itekCompanyId,
  });

  const availability = query.data?.openMasterdataAvailability;

  const [openMasterdataUpdateSupplierProduct] =
    useOpenMasterdataUpdateSupplierProductMutation({
      client,
      refetchQueries: refetchQueries,
    });

  if (!itekCompanyId) {
    return null;
  }

  if (product.__typename === "PimSupplierProduct") {
    return null;
  }

  if (query.loading) return null;
  if (query.error) return <ErrorMessage message={query.error.message} />;
  if (query.networkStatus === NetworkStatus.error)
    return <ErrorMessage message="Network error" />;

  if (!availability) return null;

  return (
    <IconButton
      color="primary"
      size="small"
      disabled={!canEdit}
      onClick={async () => {
        const { data } = await client.query<
          OpenMasterdataAvailabilityQuery,
          OpenMasterdataAvailabilityQueryVariables
        >({
          query: OpenMasterdataAvailabilityDocument,
          variables: {
            itekCompanyId,
          },
        });

        if (data && data.openMasterdataAvailability?.isAuthenticated) {
          await openMasterdataUpdateSupplierProduct({
            variables: {
              input: {
                articleNumber: product.articleNumber,
                pimSupplierId: product.supplierId,
                itekCompanyId,
              },
            },
          });
        } else {
          setIsModalOpen(true);
        }
      }}
    >
      <Tooltip
        title={t("Sync via open masterdata", {
          ns: "Product",
        })}
      >
        <Icon>
          <FontAwesomeIcon icon={faCloudDownloadAlt} />
        </Icon>
      </Tooltip>
      {isModalOpen && (
        <SyncViaOpenMasterdataAuthenticationModal
          itekCompanyId={itekCompanyId}
          openMasterdataInfo={availability}
          handleComplete={async handleClose => {
            handleClose();

            await openMasterdataUpdateSupplierProduct({
              variables: {
                input: {
                  articleNumber: product.articleNumber,
                  pimSupplierId: product.supplierId,
                  itekCompanyId,
                },
              },
            });
          }}
          handleClose={() => setIsModalOpen(false)}
        />
      )}
    </IconButton>
  );
};
